import { useState } from 'react';
import { Scissors, Check, ChevronRight, AlertTriangle } from 'lucide-react';

const NLES = [
  { id: 'fcpx', label: 'Final Cut Pro' },
  { id: 'premiere', label: 'Premiere Pro' },
  { id: 'resolve', label: 'DaVinci Resolve' },
];

/* ── shared event order + color tables ── */
const EVENT_ORDER = [
  'Girls Getting Ready', 'Guys Getting Ready', 'First Look / Photoshoot',
  'Ceremony', 'Cocktail Hour', 'Reception (Pre-Entrance)', 'Grand Entrance',
  'Speeches / Toasts', 'First Dance', 'Parent Dances', 'Open Dance Floor', 'Exit',
];

const COLOR_MAP = {
  fcpx: [
    ['Girls Getting Ready','Pink','#ec4899'],
    ['Guys Getting Ready','Teal','#14b8a6'],
    ['First Look / Photoshoot','Purple','#a855f7'],
    ['Ceremony','Blue','#3b82f6'],
    ['Cocktail Hour','Green','#22c55e'],
    ['Reception (Pre-Entrance)','Gray','#6b7280'],
    ['Grand Entrance','Blue','#3b82f6'],
    ['Speeches / Toasts','Yellow','#eab308'],
    ['First Dance','Magenta','#d946ef'],
    ['Parent Dances','Lavender','#a78bfa'],
    ['Open Dance Floor','Red','#ef4444'],
    ['Exit','Brown','#92400e'],
    ['Misc / Unassigned','Gray','#6b7280'],
  ],
  premiere: [
    ['Girls Getting Ready','Rose','#fb7185'],
    ['Guys Getting Ready','Teal','#14b8a6'],
    ['First Look / Photoshoot','Violet','#8b5cf6'],
    ['Ceremony','Cerulean','#06b6d4'],
    ['Cocktail Hour','Forest','#16a34a'],
    ['Reception (Pre-Entrance)','Blue Gray','#64748b'],
    ['Grand Entrance','Blue','#3b82f6'],
    ['Speeches / Toasts','Yellow','#eab308'],
    ['First Dance','Magenta','#d946ef'],
    ['Parent Dances','Lavender','#a78bfa'],
    ['Open Dance Floor','Red','#ef4444'],
    ['Exit','Brown','#92400e'],
    ['Misc / Unassigned','Gray','#6b7280'],
  ],
  resolve: [
    ['Girls Getting Ready','Rose','#fb7185'],
    ['Guys Getting Ready','Teal','#14b8a6'],
    ['First Look / Photoshoot','Purple','#a855f7'],
    ['Ceremony','Cerulean','#06b6d4'],
    ['Cocktail Hour','Forest','#16a34a'],
    ['Reception (Pre-Entrance)','Blue Gray','#64748b'],
    ['Grand Entrance','Blue','#3b82f6'],
    ['Speeches / Toasts','Yellow','#eab308'],
    ['First Dance','Magenta','#d946ef'],
    ['Parent Dances','Lavender','#a78bfa'],
    ['Open Dance Floor','Red','#ef4444'],
    ['Exit','Brown','#92400e'],
    ['Misc / Unassigned','Gray','#6b7280'],
  ],
};

/* ── tiny reusable pieces ── */
const SectionHead = ({ num, title }) => (
  <div className="flex items-baseline gap-2.5 mb-2.5 mt-6 first:mt-0">
    <span className="font-mono text-[10px] font-bold text-oxidized-teal/70 bg-oxidized-teal/[0.08] rounded-full w-5 h-5 flex items-center justify-center shrink-0">{num}</span>
    <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-zinc-100/90">{title}</h4>
  </div>
);

const Bullet = ({ children, indent }) => (
  <div className={`flex gap-2 ${indent ? 'ml-4' : ''} mb-1`}>
    <ChevronRight className="w-2.5 h-2.5 text-oxidized-teal/40 shrink-0 mt-[3px]" />
    <span className="text-[10.5px] text-zinc-300/80 leading-relaxed">{children}</span>
  </div>
);

const Mono = ({ children }) => <code className="font-mono text-[10px] text-oxidized-teal/90 bg-oxidized-teal/[0.06] px-1 py-0.5 rounded">{children}</code>;

const DontList = ({ items }) => (
  <div className="mt-2 mb-2 ml-1 pl-3 border-l-2 border-red-500/20">
    {items.map((t, i) => (
      <div key={i} className="flex gap-2 mb-0.5">
        <span className="text-red-400/60 text-[9px] mt-[2px]">✕</span>
        <span className="text-[10px] text-zinc-400/80">{t}</span>
      </div>
    ))}
  </div>
);

const CheckItem = ({ children }) => (
  <div className="flex gap-2 mb-1">
    <Check className="w-3 h-3 text-oxidized-teal/50 shrink-0 mt-[1px]" />
    <span className="text-[10.5px] text-zinc-300/80">{children}</span>
  </div>
);

/* ── color table ── */
const ColorTable = ({ nle }) => {
  const rows = COLOR_MAP[nle];
  return (
    <div className="rounded-lg border border-ug-border overflow-hidden mt-2">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="bg-ug-surface/80 border-b border-ug-border">
            <th className="text-left font-sans font-semibold text-ug-gold/60 uppercase tracking-wider text-[8px] px-3 py-2">Event</th>
            <th className="text-left font-sans font-semibold text-ug-gold/60 uppercase tracking-wider text-[8px] px-3 py-2">Color</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([event, label, hex], i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.015]'}>
              <td className="px-3 py-1.5 text-zinc-300/80">{event}</td>
              <td className="px-3 py-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-white/10" style={{ backgroundColor: hex }} />
                  <span className="text-zinc-400">{label}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── NLE-specific content blocks ── */
function FCPXContent() {
  return (
    <>
      {/* Deliverables */}
      <div className="p-3 rounded-lg bg-oxidized-teal/[0.04] border border-oxidized-teal/10 mb-4">
        <span className="font-sans text-[8px] font-bold uppercase tracking-widest text-oxidized-teal/60 block mb-2">Deliverables</span>
        <Bullet>Pre-synced Multicam Clips — audio synced only, no cleanup/culling/trimming</Bullet>
        <Bullet>One chronological culling Project — source clips only, color-coded by event, grouped by camera</Bullet>
      </div>

      <SectionHead num="1" title="Library & Event Setup" />
      <Bullet>Import all video and audio into a single Library</Bullet>
      <Bullet>Create Events named: <Mono>01_RAW_VIDEO</Mono>, <Mono>02_RAW_AUDIO</Mono>, <Mono>03_MULTICAM_SYNCED</Mono>, <Mono>04_CULL_PROJECT</Mono></Bullet>
      <Bullet>Do not rename original media</Bullet>

      <SectionHead num="2" title="Multicam Clips (Sync Only)" />
      <Bullet>Select all relevant camera clips + primary audio</Bullet>
      <Bullet>Create Multicam Clip — sync using Timecode (preferred) or Audio</Bullet>
      <Bullet>Open and verify sync</Bullet>
      <Bullet>Name examples: <Mono>MC_CEREMONY</Mono>, <Mono>MC_SPEECHES</Mono>, <Mono>MC_FIRST_DANCE</Mono></Bullet>
      <Bullet>Store in <Mono>03_MULTICAM_SYNCED</Mono></Bullet>
      <DontList items={['Remove angles', 'Trim clips', 'Cull footage', 'Add markers, keywords, ratings, or roles']} />

      <SectionHead num="3" title="Culling Project" />
      <Bullet>Create Project named <Mono>WEDDING_DAY_CULLED</Mono> in <Mono>04_CULL_PROJECT</Mono></Bullet>
      <Bullet>Use original camera clips only — do NOT pull from Multicam Clips</Bullet>
      <Bullet>Keep camera-recorded audio attached. No compound clips</Bullet>

      <SectionHead num="4" title="Culling Rules" />
      <div className="p-2.5 rounded-md bg-ug-surface/60 border border-ug-border mb-2 mt-1">
        <span className="text-[10px] text-zinc-100/70 leading-relaxed">For every camera clip, keep only the best usable portion(s) and remove the rest. The result should require no junk-review by the editor.</span>
      </div>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">Per clip</span>
      <Bullet>Trim garbage from the start — camera pointed down, exposure/focus checks, setup</Bullet>
      <Bullet>Trim garbage from the end — camera lowering, walking away, left running</Bullet>
      <Bullet>If multiple usable moments exist, blade out dead time</Bullet>
      <Bullet>Leave ~1–2 sec clean handles before/after usable action</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-red-400/60 block mb-1.5 mt-3">Remove</span>
      <Bullet>Out of focus / severe motion blur</Bullet>
      <Bullet>Obstructed shots, major exposure failures</Bullet>
      <Bullet>Accidental records / tests, long stretches of nothing</Bullet>
      <Bullet>Duplicate coverage — keep best instance only</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">You are NOT deciding</span>
      <DontList items={['Best angles across cameras', 'Pacing or story', 'Music, transitions, effects, stabilization, speed ramps', 'Color correction or grading']} />
      <div className="text-[10px] text-zinc-500 italic mt-1 mb-2">This is trimming, not editing.</div>

      <SectionHead num="5" title="Event Order" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 mb-2">
        {EVENT_ORDER.map((e, i) => (
          <div key={i} className="flex gap-1.5 items-baseline">
            <span className="font-mono text-[9px] text-zinc-600 w-4 text-right">{i + 1}.</span>
            <span className="text-[10px] text-zinc-300/80">{e}</span>
          </div>
        ))}
      </div>

      <SectionHead num="6" title="Grouping Rules" />
      <Bullet>Group by event, then by camera within each event</Bullet>
      <Bullet>Maintain chronological order within each camera</Bullet>
      <Bullet>Do not interleave cameras unless timing overlap requires it</Bullet>

      <SectionHead num="7" title="Color Coding" />
      <Bullet>Apply via <Mono>Modify → Appearance → Clip Color</Mono></Bullet>
      <Bullet>One color per event — same color across all cameras</Bullet>
      <Bullet>Do not substitute Roles, Keywords, or Ratings for color coding</Bullet>
      <ColorTable nle="fcpx" />

      <SectionHead num="8" title="Timeline Organization" />
      <Bullet>Timeline flows in real time</Bullet>
      <Bullet>Events visually identifiable by color</Bullet>
      <Bullet>Cameras grouped logically within each event</Bullet>
      <Bullet>No creative edits</Bullet>

      <SectionHead num="9" title="Final Check" />
      <div className="mt-1 p-3 rounded-lg bg-ug-surface/50 border border-ug-border">
        <CheckItem>Multicam Clips exist and are sync-only</CheckItem>
        <CheckItem>Culling Project uses source clips only</CheckItem>
        <CheckItem>Events are ordered correctly</CheckItem>
        <CheckItem>Camera grouping is correct</CheckItem>
        <CheckItem>Color coding matches the table above</CheckItem>
        <CheckItem>No unnecessary edits or effects applied</CheckItem>
      </div>
    </>
  );
}

function PremiereContent() {
  return (
    <>
      <div className="p-3 rounded-lg bg-oxidized-teal/[0.04] border border-oxidized-teal/10 mb-4">
        <span className="font-sans text-[8px] font-bold uppercase tracking-widest text-oxidized-teal/60 block mb-2">Deliverables</span>
        <Bullet>Pre-synced multicam sequences — audio synced only</Bullet>
        <Bullet>One chronological culling timeline — source clips only, color-coded by event, grouped by camera</Bullet>
      </div>

      <SectionHead num="1" title="Project Setup" />
      <Bullet>Import all video and audio</Bullet>
      <Bullet>Create bins: <Mono>01_RAW_VIDEO</Mono>, <Mono>02_RAW_AUDIO</Mono>, <Mono>03_MULTICAM_SYNCED</Mono>, <Mono>04_CULL_TIMELINE</Mono></Bullet>
      <Bullet>Do not rename original media</Bullet>

      <SectionHead num="2" title="Multicam Sequences (Sync Only)" />
      <Bullet>Select all relevant video clips + primary audio</Bullet>
      <Bullet>Sync by Timecode (preferred) or Audio Waveform</Bullet>
      <Bullet>Create multicam sequence, lay synced audio underneath</Bullet>
      <Bullet>Name examples: <Mono>MC_CEREMONY</Mono>, <Mono>MC_SPEECHES</Mono>, <Mono>MC_FIRST_DANCE</Mono></Bullet>
      <Bullet>Store in <Mono>03_MULTICAM_SYNCED</Mono></Bullet>
      <DontList items={['Remove clips', 'Trim clips', 'Cull footage', 'Apply labels or markers']} />

      <SectionHead num="3" title="Culling Timeline" />
      <Bullet>Create a sequence named <Mono>WEDDING_DAY_CULLED</Mono></Bullet>
      <Bullet>Use original camera clips only — do NOT pull from multicam sequences</Bullet>
      <Bullet>Keep camera audio attached to each clip</Bullet>

      <SectionHead num="4" title="Culling Rules" />
      <div className="p-2.5 rounded-md bg-ug-surface/60 border border-ug-border mb-2 mt-1">
        <span className="text-[10px] text-zinc-100/70 leading-relaxed">For every camera clip, keep only the best usable portion(s) and remove the rest. The goal is a timeline where an editor can start cutting immediately without wading through junk.</span>
      </div>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">Per clip</span>
      <Bullet>Trim off garbage at the start — camera at floor, exposure adjustments, setup</Bullet>
      <Bullet>Trim off garbage at the end — lowering camera, walking away, left running</Bullet>
      <Bullet>If clip has multiple moments separated by dead time, blade out dead time</Bullet>
      <Bullet>Leave ~1–2 sec clean handles before/after usable moments</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-red-400/60 block mb-1.5 mt-3">Remove</span>
      <Bullet>Obstructed shots, out of focus / severe motion blur</Bullet>
      <Bullet>Major exposure mistakes (blown out, too dark to recover)</Bullet>
      <Bullet>Accidental records / tests, long stretches of nothing</Bullet>
      <Bullet>Duplicate "same exact thing" — keep the best part</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">You are NOT deciding</span>
      <DontList items={['Best camera angle across cameras', 'Pacing or story', 'Music, transitions, effects, stabilization, speed ramps', 'Color grading']} />
      <div className="text-[10px] text-zinc-500 italic mt-1 mb-2">You are trimming clips down to the best usable pieces, not editing the film.</div>

      <SectionHead num="5" title="Event Order" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 mb-2">
        {EVENT_ORDER.map((e, i) => (
          <div key={i} className="flex gap-1.5 items-baseline">
            <span className="font-mono text-[9px] text-zinc-600 w-4 text-right">{i + 1}.</span>
            <span className="text-[10px] text-zinc-300/80">{e}</span>
          </div>
        ))}
      </div>

      <SectionHead num="6" title="Grouping Rules" />
      <Bullet>Group by event, then by camera within each event</Bullet>
      <Bullet>Maintain chronological order within each camera</Bullet>
      <Bullet>Do not interleave cameras unless time overlap requires it</Bullet>
      <Bullet>All cameras covering the same event must use the same color label</Bullet>

      <SectionHead num="7" title="Color Coding" />
      <Bullet>Apply clip labels in the culling timeline only</Bullet>
      <Bullet>One color per event — same color across all cameras</Bullet>
      <ColorTable nle="premiere" />

      <SectionHead num="8" title="Timeline Organization" />
      <Bullet>Timeline flows in real time</Bullet>
      <Bullet>Events visually identifiable by color</Bullet>
      <Bullet>Cameras grouped logically within each event</Bullet>
      <Bullet>No creative edits</Bullet>

      <SectionHead num="9" title="Final Check" />
      <div className="mt-1 p-3 rounded-lg bg-ug-surface/50 border border-ug-border">
        <CheckItem>Multicam sequences exist and are synced only</CheckItem>
        <CheckItem>Culling timeline uses source clips only</CheckItem>
        <CheckItem>Events are grouped, ordered, and color-coded correctly</CheckItem>
        <CheckItem>No unnecessary edits applied</CheckItem>
      </div>
    </>
  );
}

function ResolveContent() {
  return (
    <>
      <div className="p-3 rounded-lg bg-oxidized-teal/[0.04] border border-oxidized-teal/10 mb-4">
        <span className="font-sans text-[8px] font-bold uppercase tracking-widest text-oxidized-teal/60 block mb-2">Deliverables</span>
        <Bullet>Pre-synced Multicam Clips — audio synced only, no cleanup/culling/trimming</Bullet>
        <Bullet>One chronological culling Timeline — source clips only, color-coded by event, grouped by camera</Bullet>
      </div>

      <SectionHead num="1" title="Project & Media Pool Setup" />
      <Bullet>Create a new Resolve Project</Bullet>
      <Bullet>Import all video and audio into the Media Pool</Bullet>
      <Bullet>Create Bins: <Mono>01_RAW_VIDEO</Mono>, <Mono>02_RAW_AUDIO</Mono>, <Mono>03_MULTICAM_SYNCED</Mono>, <Mono>04_CULL_TIMELINE</Mono></Bullet>
      <Bullet>Do not rename original media files</Bullet>

      <SectionHead num="2" title="Multicam Clips (Sync Only)" />
      <Bullet>Select all relevant camera clips + primary audio in the Media Pool</Bullet>
      <Bullet>Right-click → <Mono>Create Multicam Clip Using Selected Clips</Mono></Bullet>
      <Bullet>Sync using Timecode (preferred) or Sound</Bullet>
      <Bullet>Open and confirm proper sync</Bullet>
      <Bullet>Name examples: <Mono>MC_CEREMONY</Mono>, <Mono>MC_SPEECHES</Mono>, <Mono>MC_FIRST_DANCE</Mono></Bullet>
      <Bullet>Store in <Mono>03_MULTICAM_SYNCED</Mono></Bullet>
      <DontList items={['Trim clips', 'Remove angles', 'Cull footage', 'Add markers', 'Add color grading', 'Modify clip attributes']} />

      <SectionHead num="3" title="Culling Timeline" />
      <Bullet>Create a Timeline named <Mono>WEDDING_DAY_CULLED</Mono> in <Mono>04_CULL_TIMELINE</Mono></Bullet>
      <Bullet>Use original camera clips only — do NOT pull from Multicam Clips</Bullet>
      <Bullet>Keep camera-recorded audio attached</Bullet>
      <Bullet>No compound clips, no nested timelines</Bullet>

      <SectionHead num="4" title="Culling Rules" />
      <div className="p-2.5 rounded-md bg-ug-surface/60 border border-ug-border mb-2 mt-1">
        <span className="text-[10px] text-zinc-100/70 leading-relaxed">For every camera clip, keep only the best usable portion(s) and remove the rest. The result should require no junk review before editing.</span>
      </div>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">Per clip</span>
      <Bullet>Trim garbage from the start — camera pointed down, exposure/focus checks, setup</Bullet>
      <Bullet>Trim garbage from the end — camera lowering, walking away, left recording</Bullet>
      <Bullet>If a clip has multiple usable moments, blade out dead time</Bullet>
      <Bullet>Leave ~1–2 sec clean handles before/after usable action</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-red-400/60 block mb-1.5 mt-3">Remove</span>
      <Bullet>Out of focus / severe motion blur</Bullet>
      <Bullet>Obstructed shots, major exposure failures (unrecoverable)</Bullet>
      <Bullet>Accidental records / tests, long stretches of nothing</Bullet>
      <Bullet>Duplicate coverage — keep best instance only</Bullet>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-zinc-400/70 block mb-1.5 mt-3">You are NOT deciding</span>
      <DontList items={['Best angles across cameras', 'Pacing or story', 'Music, transitions, effects, stabilization, speed ramps', 'Color correction or grading']} />
      <div className="text-[10px] text-zinc-500 italic mt-1 mb-2">This is trimming, not editing.</div>

      <SectionHead num="5" title="Event Order" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 mb-2">
        {EVENT_ORDER.map((e, i) => (
          <div key={i} className="flex gap-1.5 items-baseline">
            <span className="font-mono text-[9px] text-zinc-600 w-4 text-right">{i + 1}.</span>
            <span className="text-[10px] text-zinc-300/80">{e}</span>
          </div>
        ))}
      </div>

      <SectionHead num="6" title="Grouping Rules" />
      <Bullet>Group by event, then by camera within each event</Bullet>
      <Bullet>Maintain chronological order within each camera</Bullet>
      <Bullet>Do not interleave cameras unless timing overlap requires it</Bullet>
      <Bullet>All clips from the same event must share the same clip color</Bullet>

      <SectionHead num="7" title="Color Coding" />
      <Bullet>Apply via <Mono>Right-click clip → Clip Color</Mono></Bullet>
      <Bullet>One color per event — same color across all cameras</Bullet>
      <Bullet>Do not use flags, markers, or metadata as a substitute for color</Bullet>
      <ColorTable nle="resolve" />

      <SectionHead num="8" title="Timeline Organization" />
      <Bullet>Timeline flows in real time</Bullet>
      <Bullet>Events visually identifiable by color</Bullet>
      <Bullet>Cameras grouped logically within each event</Bullet>
      <Bullet>No creative edits</Bullet>

      <SectionHead num="9" title="Final Check" />
      <div className="mt-1 p-3 rounded-lg bg-ug-surface/50 border border-ug-border">
        <CheckItem>Multicam Clips exist and are sync-only</CheckItem>
        <CheckItem>Culling Timeline uses source clips only</CheckItem>
        <CheckItem>Events are ordered correctly</CheckItem>
        <CheckItem>Camera grouping is correct</CheckItem>
        <CheckItem>Color coding matches the table above</CheckItem>
        <CheckItem>No unnecessary edits or effects applied</CheckItem>
      </div>
    </>
  );
}

const NLE_CONTENT = { fcpx: FCPXContent, premiere: PremiereContent, resolve: ResolveContent };

export default function CullingInstructions() {
  const [nle, setNle] = useState('fcpx');
  const Content = NLE_CONTENT[nle];

  return (
    <div className="p-5" data-testid="culling-instructions">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Scissors className="w-4 h-4 text-oxidized-teal/70" />
        <h3 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-zinc-100/90">Culling Instructions</h3>
      </div>

      {/* Scope notice */}
      <div className="p-3 rounded-lg bg-ug-gold/[0.06] border border-ug-gold/20 mb-5 flex gap-2.5" data-testid="culling-scope-notice" data-tone="warning">
        <AlertTriangle className="w-4 h-4 text-ug-gold/70 shrink-0 mt-0.5" />
        <p className="text-[11px] text-zinc-300/90 leading-relaxed">
          <strong className="text-ug-gold/90">Culling-Only jobs only.</strong> These instructions apply when you are assigned a standalone Culling project. For regular editing jobs (highlights, docs, features, etc.) you do <em>not</em> need to follow this workflow.
        </p>
      </div>

      {/* Segmented NLE Selector */}
      <div className="flex bg-ug-surface/80 rounded-lg p-0.5 border border-ug-border mb-5" data-testid="nle-selector">
        {NLES.map(n => (
          <button
            key={n.id}
            data-testid={`nle-${n.id}`}
            onClick={() => setNle(n.id)}
            className={`flex-1 text-center font-sans text-[10px] font-medium tracking-wide py-2 rounded-md transition-all duration-200 ${
              nle === n.id
                ? 'bg-oxidized-teal/15 text-oxidized-teal shadow-sm border border-oxidized-teal/20'
                : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
            }`}
          >
            {n.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <Content />
    </div>
  );
}
